import { useState } from 'react';
import { Sparkles, Wrench, Bug } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { cn } from '../../../lib/utils';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { openExternalUrl } from '../../help-feedback';
import type { ChangeCategory, ChangelogChange, ChangelogEntry } from '../data/changelog';
import { changeText } from '../data/changelog';
import { ChangelogDetailDialog, type ChangelogLinkTarget } from './ChangelogDetailDialog';

const categoryConfig: Record<ChangeCategory, { label: string; icon: typeof Sparkles; className: string }> = {
  feature: {
    label: 'New',
    icon: Sparkles,
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800',
  },
  improvement: {
    label: 'Improved',
    icon: Wrench,
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800',
  },
  fix: {
    label: 'Fixed',
    icon: Bug,
    className: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300 border-green-200 dark:border-green-800',
  },
};

interface ChangelogContentProps {
  entries: ChangelogEntry[];
}

type ActiveDetail = { change: ChangelogChange; version: string };

export function ChangelogContent({ entries }: ChangelogContentProps) {
  const { track } = useAnalytics();
  const [active, setActive] = useState<ActiveDetail | null>(null);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No changelog entries yet.</p>
    );
  }

  const openDetail = (change: ChangelogChange, version: string) => {
    setActive({ change, version });
    track('feature:changelog_show_more', {
      version,
      entryId: change.id ?? '',
      category: change.category,
    });
  };

  const handleLinkClick = (target: ChangelogLinkTarget) => {
    if (!active) return;
    track('feature:changelog_link_click', {
      version: active.version,
      entryId: active.change.id ?? '',
      target,
    });
  };

  const openInlineLink = (change: ChangelogChange, version: string, target: ChangelogLinkTarget, url: string) => {
    track('feature:changelog_link_click', { version, entryId: change.id ?? '', target });
    openExternalUrl(url);
  };

  return (
    <div className="space-y-6">
      {entries.map((entry) => (
        <div key={entry.version}>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-base font-semibold">v{entry.version}</span>
            <span className="text-xs text-muted-foreground">{entry.date}</span>
          </div>
          {entry.highlights && (
            <p className="text-sm text-muted-foreground mb-2">{entry.highlights}</p>
          )}
          <ul className="space-y-1.5">
            {entry.changes.map((change, i) => {
              const config = categoryConfig[change.category];
              const Icon = config.icon;
              return (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Badge
                    variant="outline"
                    className={cn('shrink-0 gap-1 text-[10px] px-1.5 py-0', config.className)}
                  >
                    <Icon className="h-3 w-3" />
                    {config.label}
                  </Badge>
                  <span className="whitespace-pre-line">
                    {changeText(change)}
                    {change.details ? (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => openDetail(change, entry.version)}
                          className="text-primary underline underline-offset-2 hover:no-underline whitespace-nowrap"
                          data-testid="changelog-show-more"
                        >
                          Show more info…
                        </button>
                      </>
                    ) : (change.docsUrl || change.blogUrl) ? (
                      <>
                        {'  '}
                        {change.docsUrl && (
                          <button
                            type="button"
                            onClick={() => openInlineLink(change, entry.version, 'docs', change.docsUrl as string)}
                            className="text-primary underline underline-offset-2 hover:no-underline whitespace-nowrap"
                            data-testid="changelog-inline-docs"
                          >
                            Docs
                          </button>
                        )}
                        {change.docsUrl && change.blogUrl && <span className="text-muted-foreground"> · </span>}
                        {change.blogUrl && (
                          <button
                            type="button"
                            onClick={() => openInlineLink(change, entry.version, 'blog', change.blogUrl as string)}
                            className="text-primary underline underline-offset-2 hover:no-underline whitespace-nowrap"
                            data-testid="changelog-inline-blog"
                          >
                            Blog post
                          </button>
                        )}
                      </>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <ChangelogDetailDialog
        change={active?.change ?? null}
        onClose={() => setActive(null)}
        onLinkClick={handleLinkClick}
      />
    </div>
  );
}
